import { useCallback, useEffect, useState } from 'react';
import {
  fetchTransferState,
  type Decision,
} from 'src/services/DataVaultTransfer.service';
import { decisionKey, type TransferPageState } from '../types';

interface UseTransferStateResult {
  state: TransferPageState;
  loadState: () => Promise<void>;
  updateDecision: (decision: Decision) => void;
  removeDecision: (qitId: number, instanceIndex: number) => void;
}

interface UseTransferStateOptions {
  /**
   * Called when the transfer is already done (committed) on page load.
   * The caller is responsible for navigating to the next step — using
   * fetchContinueCertification so the resolver picks the right surface.
   * If not provided the hook does nothing (caller controls navigation).
   */
  onAlreadyDone?: () => void;
}

export function useTransferState(
  surveyId: number | undefined,
  { onAlreadyDone }: UseTransferStateOptions = {}
): UseTransferStateResult {
  const [state, setState] = useState<TransferPageState>({
    status: 'loading',
    transferId: null,
    proposals: [],
    decisions: new Map(),
    summary: null,
    error: null,
  });

  const loadState = useCallback(async () => {
    if (!surveyId || !Number.isFinite(surveyId)) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: 'Invalid survey ID',
      }));
      return;
    }

    try {
      const data = await fetchTransferState(surveyId);

      if (data.redirect_to === 'questionnaire') {
        // Transfer already committed — delegate routing to the caller so the
        // resolver picks the right next surface (data-transfer reload edge
        // case vs. IPE).
        onAlreadyDone?.();
        return;
      }

      const decisionsMap = new Map<string, Decision>();
      if (data.decisions) {
        for (const d of data.decisions) {
          decisionsMap.set(decisionKey(d.qit_id, d.instance_index), d);
        }
      }

      setState({
        status: data.status as TransferPageState['status'],
        transferId: data.transfer_id,
        proposals: data.proposals || [],
        decisions: decisionsMap,
        summary: data.summary || { total: 0, decided: 0, remaining: 0 },
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error:
          err instanceof Error ? err.message : 'Failed to load transfer state',
      }));
    }
  }, [surveyId, onAlreadyDone]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const updateDecision = useCallback((decision: Decision) => {
    setState((prev) => {
      const newDecisions = new Map(prev.decisions);
      newDecisions.set(
        decisionKey(decision.qit_id, decision.instance_index),
        decision
      );
      const decided = newDecisions.size;
      return {
        ...prev,
        decisions: newDecisions,
        summary: prev.summary
          ? {
              ...prev.summary,
              decided,
              remaining: prev.summary.total - decided,
            }
          : null,
      };
    });
  }, []);

  const removeDecision = useCallback((qitId: number, instanceIndex: number) => {
    setState((prev) => {
      const newDecisions = new Map(prev.decisions);
      newDecisions.delete(decisionKey(qitId, instanceIndex));
      const decided = newDecisions.size;
      return {
        ...prev,
        decisions: newDecisions,
        summary: prev.summary
          ? {
              ...prev.summary,
              decided,
              remaining: prev.summary.total - decided,
            }
          : null,
      };
    });
  }, []);

  return { state, loadState, updateDecision, removeDecision };
}
